import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IServiceGraderPoolProvider, IServiceGraderPoolProviderServiceProvider } from '@common/interfaces';
import type { ISelectOption } from '@frontend/components/elements/common';
import type { IServiceProvider } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { BittiumRecordingAnalyst } from '@common/enums/bittiumRecordingAnalyst';
import { getDisplayName } from '@common/utils/getDisplayName';
import { serviceGraderPoolAddEditSchema } from '@common/validation/serviceGraderPool';
import {
    Button,
    ButtonContainer,
    CancelButton,
    Dropdown,
    FormContainer,
    FormGroup,
    FormLabel,
    InfoTooltip,
    InputGroup,
    InputText,
    SaveSubmit,
    SecondHeader,
} from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { FixedWidthTemplate } from '@frontend/components/templates/FixedWidthTemplate';
import { deleteJson, fetchJson, getJson, postJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IServiceGraderPool } from '../../interfaces/serviceGraderPoolInterfaces';
import { ServiceGraderPoolProviderList } from './ServiceGraderPoolProviderList';

interface IServiceGraderPoolProviderWithUnsaved extends IServiceGraderPoolProvider {
    unsaved: boolean | null;
}

export function ServiceGraderPoolAddEdit() {
    assertHasAuthorization(ManagerAuthorization.graderPools);
    const navigate = useNavigate();
    const { id } = useParams() as { id: string };
    const [searchParams] = useSearchParams();
    const serviceId = searchParams.get('serviceId') ?? '';

    const [errors, setErrors] = useState(emptyValidationErrors);
    const [serviceGraderPool, setServiceGraderPool] = useState<IServiceGraderPool>({
        name: '',
        serviceId: serviceId,
        serviceProviderId: '',
        canGradeForAnalyst: null,
    });
    const [serviceGraderPoolProviders, setServiceGraderPoolProviders] = useState<
        IServiceGraderPoolProviderWithUnsaved[]
    >([]);
    const [serviceProviders, setServiceProviders] = useState<IServiceProvider[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const [serviceProviderSelectOptions, setServiceProviderSelectOptions] = useState<ISelectOption[]>([]);

    useEffect(() => {
        loadAll(id);
    }, [id]);

    useEffect(() => {
        setServiceProviderSelectOptions(getServiceProvidersSelectOptions());
    }, [serviceProviders, serviceGraderPoolProviders]);

    useEffect(() => {
        if (serviceProviderSelectOptions.length > 0) {
            setServiceGraderPool({
                ...serviceGraderPool,
                serviceProviderId: serviceProviderSelectOptions[0].value,
            });
        }
    }, [serviceProviderSelectOptions]);

    if (!serviceGraderPool || !serviceProviders.length) {
        return null;
    }
    const canGradeForAnalystSelectOptions = getCanGradeForAnalystSelectOptions();

    return (
        <FixedWidthTemplate>
            <FormContextProvider
                value={{
                    placeholderGroupName: 'common',
                    item: serviceGraderPool,
                    errors: errors,
                    onChange: handleChange,
                    onDoValidate: validateOne,
                }}
            >
                <SecondHeader
                    text={translateKey(
                        id ? 'serviceGraderPool.editServiceGraderPool' : 'serviceGraderPool.newServiceGraderPool'
                    )}
                />
                <FormContainer onSubmit={saveServiceGraderPool}>
                    <FormGroup hasErrors={errors.has('name')}>
                        <FormLabel isRequired>{translateKey('serviceGraderPool.name')}</FormLabel>
                        <InputGroup>
                            <InputText
                                error={errors.get('name')}
                                fieldKey="name"
                            />
                        </InputGroup>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>
                            {translateKey('serviceGraderPool.canGradeForAnalyst')}
                            <InfoTooltip text={translateKey('serviceGraderPool.toolTipText')} />
                        </FormLabel>
                        <InputGroup>
                            <Dropdown
                                fieldKey="canGradeForAnalyst"
                                placeholder={translateKey('serviceGraderPool.chooseOption')}
                                selectOptions={canGradeForAnalystSelectOptions}
                            />
                        </InputGroup>
                    </FormGroup>

                    <FormGroup hasErrors={errors.has('serviceId')}>
                        <FormLabel isRequired>{translateKey('serviceGraderPool.addProvider')}</FormLabel>
                        <InputGroup>
                            <Dropdown
                                fieldKey="serviceProviderId"
                                placeholder=""
                                selectOptions={serviceProviderSelectOptions}
                            />
                            <Button
                                color="secondary"
                                disabled={serviceProviderSelectOptions.length === 0}
                                onClick={addGraderToGraderPool}
                                outlined
                                right
                            >
                                Toevoegen
                            </Button>
                        </InputGroup>
                    </FormGroup>

                    <ServiceGraderPoolProviderList
                        onDeleteRow={deleteRow}
                        onEditRow={editRow}
                        serviceGraderPoolId={id}
                        serviceGraderPoolProviders={serviceGraderPoolProviders}
                        serviceId={serviceId}
                    />
                    <ButtonContainer>
                        <CancelButton
                            disabled={isSaving}
                            to={`/services/${serviceId || serviceGraderPool.serviceId}?tab=graderPool`}
                        />
                        <SaveSubmit disabled={isSaving} />
                    </ButtonContainer>
                </FormContainer>
            </FormContextProvider>
        </FixedWidthTemplate>
    );

    async function loadAll(graderPoolId?: string) {
        if (graderPoolId) {
            await Promise.all([
                fetchServiceGraderPool(),
                loadGraderPoolProviders(graderPoolId),
                loadActiveServiceProviders(),
            ]);
        } else {
            await loadActiveServiceProviders();
        }
    }

    async function handleChange<K extends keyof IServiceGraderPool>(key: K, value: IServiceGraderPool[K]) {
        setServiceGraderPool({ ...serviceGraderPool, [key]: value });
    }

    async function validateOne(key: keyof IServiceGraderPool) {
        setErrors(updateValidationErrorsForOne(serviceGraderPoolAddEditSchema, serviceGraderPool, errors, key));
    }

    async function addGraderToGraderPool() {
        const { serviceProviderId } = serviceGraderPool;
        if (!serviceProviderId) {
            return;
        }

        const serviceProvider = serviceProviders.find(
            (serviceProviderItem) => serviceProviderItem.id === serviceProviderId
        );
        if (serviceProvider) {
            const mappedServiceProvider: IServiceGraderPoolProviderServiceProvider = {
                employmentId: '',
                employment: {
                    organizationId: serviceProvider.employment.organization.id,
                    organization: {
                        name: serviceProvider.employment.organization.name || '',
                    },
                    practitionerId: serviceProvider.employment.practitioner.id,
                    practitioner: {
                        nameFamily: serviceProvider.employment.practitioner.nameFamily,
                        nameGiven: serviceProvider.employment.practitioner.nameGiven,
                        nameInfix: serviceProvider.employment.practitioner.nameInfix,
                    },
                },
            };

            const serviceGraderPoolProvider: IServiceGraderPoolProviderWithUnsaved = {
                id: '',
                serviceGraderPoolId: '',
                maxGradingsPerDay: null,
                maxGradingsPerMonth: null,
                serviceProviderId,
                serviceProvider: mappedServiceProvider,
                unsaved: true,
            };

            setServiceGraderPoolProviders([...serviceGraderPoolProviders, serviceGraderPoolProvider]);
        }

        if (errors.has('serviceId')) {
            setErrors(errors.remove('serviceId'));
        }
    }

    async function saveServiceGraderPool() {
        const serviceGraderPoolValidated = validate(serviceGraderPoolAddEditSchema, serviceGraderPool);

        let map = serviceGraderPoolValidated.errors;

        map = serviceGraderPoolValidated.errors;

        if (serviceGraderPoolProviders.length === 0) {
            map = map.set('serviceId', 'noServiceGraderPoolProviders');
        }

        if (map.size) {
            setErrors(map);

            return;
        }

        setIsSaving(true);
        let url = '/api/serviceGraderPools';
        if (serviceGraderPool.id) {
            url += `/${serviceGraderPool.id}`;
        }
        const data = {
            name: serviceGraderPool.name,
            canGradeForAnalyst: serviceGraderPool.canGradeForAnalyst,
            serviceId: serviceGraderPool.serviceId,
        };
        const { json } = await postJson(url, data);
        await saveServiceGraderPoolProviders(json);
        setIsSaving(false);
        navigate(`/services/${serviceGraderPoolValidated.item!.serviceId}?tab=graderPool`);
    }

    async function saveServiceGraderPoolProviders(graderPool: IServiceGraderPool) {
        const url = '/api/serviceGraderPoolProviders';
        const newServiceGraderPoolsPromises = serviceGraderPoolProviders
            .filter((serviceGraderPoolProvider) => serviceGraderPoolProvider.unsaved)
            .map(async (newServiceGraderPool) => {
                const data = {
                    serviceGraderPoolId: graderPool.id,
                    serviceProviderId: newServiceGraderPool.serviceProviderId,
                    maxGradingsPerDay: newServiceGraderPool.maxGradingsPerDay,
                    maxGradingsPerMonth: newServiceGraderPool.maxGradingsPerMonth,
                };

                return postJson(url, data);
            });

        await Promise.all(newServiceGraderPoolsPromises);
    }

    function getServiceProvidersSelectOptions() {
        return serviceProviders
            .filter((serviceProvider) => {
                return !serviceGraderPoolProviders.find((serviceGraderPoolProvider) => {
                    return serviceGraderPoolProvider.serviceProviderId === serviceProvider.id;
                });
            })
            .map((serviceProvider) => {
                const practitioner = getDisplayName(serviceProvider.employment.practitioner);
                const organization = serviceProvider.employment.organization.name;

                return {
                    value: serviceProvider.id,
                    text: `${practitioner}, ${organization}`,
                };
            });
    }

    function getCanGradeForAnalystSelectOptions() {
        return Object.values(BittiumRecordingAnalyst).map((value) => {
            return {
                value,
                text: translateKey(`bittiumRecordingAnalystEnum.${value}`),
            };
        });
    }

    async function loadActiveServiceProviders() {
        const url = `/api/serviceProviders?serviceId=${serviceId}&isActive=true&isGrader=true`;

        const result = await getJson(url);
        if (result.json.items.length) {
            setServiceProviders(result.json.items);
        }
    }

    async function editRow(
        serviceProviderId: string,
        maxGradingsPerDay: number | null,
        maxGradingsPerMonth: number | null
    ) {
        const serviceGraderPoolProvider = serviceGraderPoolProviders.find(
            (p) => p.serviceProviderId === serviceProviderId
        );
        if (serviceGraderPoolProvider?.id) {
            const url = `/api/serviceGraderPoolProviders/${serviceGraderPoolProvider.id}`;
            await fetchJson(url, 'POST', { maxGradingsPerDay, maxGradingsPerMonth });
            await loadGraderPoolProviders();
        } else {
            const editedServiceGraderPoolProviders = serviceGraderPoolProviders.map((provider) => {
                if (provider.serviceProviderId === serviceProviderId) {
                    return { ...provider, maxGradingsPerDay, maxGradingsPerMonth };
                }

                return provider;
            });
            setServiceGraderPoolProviders(editedServiceGraderPoolProviders);
        }
    }

    async function deleteRow(serviceGraderPoolProviderId: string, serviceProviderId: string) {
        if (serviceGraderPoolProviderId) {
            const url = `/api/serviceGraderPoolProviders/${serviceGraderPoolProviderId}`;
            await deleteJson(url);
            await loadGraderPoolProviders(serviceGraderPool.id);
        } else {
            const filteredServiceGraderPoolProviders = serviceGraderPoolProviders.filter(
                (serviceGraderPoolProvider) => {
                    return serviceGraderPoolProvider.serviceProviderId !== serviceProviderId;
                }
            );

            setServiceGraderPoolProviders(filteredServiceGraderPoolProviders);
        }
    }

    async function loadGraderPoolProviders(serviceGraderPoolId?: string) {
        const url = `/api/serviceGraderPoolProviders?serviceGraderPoolId=${id || serviceGraderPoolId}`;
        const result = await getJson(url);
        const retrievedServiceGraderPoolProviders: IServiceGraderPoolProviderWithUnsaved[] = result.json.items.map(
            (item: IServiceGraderPoolProviderWithUnsaved) => ({ ...item, unsaved: false })
        );

        if (retrievedServiceGraderPoolProviders) {
            const serviceGraderPoolProvidersFiltered = serviceGraderPoolProviders.filter(
                (serviceGraderPoolProvider) => serviceGraderPoolProvider.unsaved
            );
            serviceGraderPoolProvidersFiltered.push(...retrievedServiceGraderPoolProviders);

            setServiceGraderPoolProviders(serviceGraderPoolProvidersFiltered);
        }
    }

    async function fetchServiceGraderPool() {
        const url = `/api/serviceGraderPools/${id}`;
        const result = await getJson(url);

        setServiceGraderPool({ ...result.json, serviceProviderId: '' });
    }
}
